/**
 * FLOW: file scan, from file picker to rendered results.
 *
 *   1. `triggerFileInput` / drag-drop → `handleFileSelect` → `submitFile`.
 *   2. `submitFile` turns the checkbox state into the `rule` header string (`buildScanRules`) and calls
 *      `scan()` from `services/api/FileUpload` → `POST /v4/file` with the file as multipart form data.
 *      Sandbox, private processing and archive passwords ride along as headers.
 *      Upload progress comes from axios `onUploadProgress`, not from the backend.
 *   3. On a response with a `data_id`, `useFileScan` navigates to
 *      `/results/file/{data_id}/{tab}` and invalidates the sha1-keyed SWR entries (sandbox,
 *      vulnerability, scan history, comments) so the results page refetches instead of showing the
 *      previous file's cache.
 *   4. The results page polls: `useFileData` (`services/api/Results`) re-fetches `/v4/file/{data_id}`
 *      every 3s until `getFileComplete()` is true, then stops (`refreshInterval → 0`). Sandbox data
 *      polls separately via `sandboxRefreshInterval` (10s while `scan_in_progress`, 3s otherwise, 0
 *      once there is a final verdict / 100% / `Aborted`). No WebSocket is involved in scanning —
 *      the only subscription in the app is notifications.
 *
 * This hook never uploads a hash: a hash is not a file. It re-exports `lookup` / `lookupSandbox` from
 * `useFileScan` so a card whose single input accepts either kind of value can route hash input to the
 * reputation endpoint, exactly as `useThreatAnalyze` does (see the FLOW block there). The decision of
 * which branch an input takes belongs to the card — `PublicScanCard.handleUrlSubmit` is the reference —
 * because only the card knows what its input box accepts.
 *
 * Branch conditions worth knowing:
 *   - `rulesOverride` / `sandboxOverride` let a caller (e.g. a rescan with different options) bypass the
 *     checkbox state for one submission.
 *   - The interlock in `handleCheckboxChange` + `isUnarchivingDisabled`: with Deep CDR on and
 *     multiscanning off, unarchiving is forced on and its checkbox is locked, and `buildScanRules`
 *     collapses the whole string to the single `cdr` rule — that combination is expressed to the
 *     backend as CDR-only, so the individual rule flags are not sent alongside it.
 *   - Each `submitFile` aborts the previous upload — switching tabs unmounts the card, and a late
 *     response must not update state or fire analytics for a card that is gone.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useFileScan } from 'services/api/FileUpload';
import { useGTM } from 'hooks/useGTM';

export type ScanOptions = {
    multiscanning: boolean;
    deepCdr: boolean;
    adaptiveSandbox: boolean;
    proactiveDlp: boolean;
    unarchiving: boolean;
    privateScan: boolean;
};

export type ScanOptionKey = keyof ScanOptions;

export const defaultScanOptions: ScanOptions = {
    multiscanning: true,
    deepCdr: false,
    adaptiveSandbox: false,
    proactiveDlp: false,
    unarchiving: false,
    privateScan: false,
};

/**
 * Defaults for guests / non-paid users: every engine enabled to showcase the full
 * pipeline. Private Scan stays off since it remains a paid-only capability.
 */
export const freeScanOptions: ScanOptions = {
    multiscanning: true,
    deepCdr: true,
    adaptiveSandbox: true,
    proactiveDlp: true,
    unarchiving: true,
    privateScan: false,
};

/**
 * Build the comma-separated rules string consumed by the file scan API.
 * Exported because some callers need rules ahead of scan invocation.
 */
/**
 * Every technology token `buildScanRules` can emit. A scan submitted with a custom workflow sends the
 * workflow's display name as the `rule` header instead, so a reader of that header needs this to tell
 * "the user asked for these technologies" from "this is a workflow name that says nothing about them".
 */
export const SCAN_RULE_TOKENS = ['cdr', 'sanitize', 'dlp', 'multiscan', 'unarchive'];

export const buildScanRules = (scanOptions: ScanOptions): string => {
    const isCdrOnly =
        scanOptions.deepCdr && !scanOptions.multiscanning && !scanOptions.proactiveDlp && !scanOptions.adaptiveSandbox;
    const shouldSanitizeForCdr =
        scanOptions.deepCdr &&
        (scanOptions.multiscanning ||
            scanOptions.proactiveDlp ||
            scanOptions.unarchiving ||
            scanOptions.adaptiveSandbox);

    if (isCdrOnly) return 'cdr';
    const rules: string[] = [];
    if (shouldSanitizeForCdr) {
        rules.push('sanitize');
        if (scanOptions.proactiveDlp) rules.push('dlp');
    } else {
        if (scanOptions.multiscanning) rules.push('multiscan');
        if (scanOptions.proactiveDlp) rules.push('dlp');
    }
    if (scanOptions.unarchiving) rules.push('unarchive');
    return rules.join(', ');
};

type UseFileScanFlowOptions = {
    initialOptions?: ScanOptions;
    /** localStorage key for persisting scanOptions. If omitted, state is in-memory only. */
    persistKey?: string;
};

const readPersistedOptions = (key: string | undefined, fallback: ScanOptions): ScanOptions => {
    if (!key || typeof window === 'undefined') return fallback;
    try {
        const saved = localStorage.getItem(key);
        if (!saved) return fallback;
        return { ...fallback, ...JSON.parse(saved) };
    } catch {
        return fallback;
    }
};

/**
 * Shared file-scan flow used by AnalysisFileCard (authenticated) and PublicScanCard (public landing).
 *
 * Covers:
 *  - scan options state + persistence
 *  - the multiscanning <-> deepCdr <-> unarchiving interlock
 *  - the buildRules string
 *  - file input ref / click-trigger / change handler
 *  - submitting the file via useFileScan + tracking progress
 */
export const useFileScanFlow = (options: UseFileScanFlowOptions = {}) => {
    const { initialOptions = defaultScanOptions, persistKey } = options;

    const { scan, scanUrl, lookup, lookupSandbox, isLoading } = useFileScan();
    const { trackCustomEvent } = useGTM();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const abortControllerRef = useRef<AbortController | null>(null);
    const isMountedRef = useRef(true);

    const [scanOptions, setScanOptions] = useState<ScanOptions>(() => readPersistedOptions(persistKey, initialOptions));
    const [isUploaded, setIsUploaded] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [fileName, setFileName] = useState('');
    const [fileSize, setFileSize] = useState(0);

    useEffect(() => {
        if (!persistKey || typeof window === 'undefined') return;
        localStorage.setItem(persistKey, JSON.stringify(scanOptions));
    }, [persistKey, scanOptions]);

    useEffect(() => {
        return () => {
            isMountedRef.current = false;
            abortControllerRef.current?.abort();
        };
    }, []);

    const isUnarchivingDisabled = scanOptions.deepCdr && !scanOptions.multiscanning;

    const handleCheckboxChange = useCallback((option: ScanOptionKey) => {
        setScanOptions((prev) => {
            const next = { ...prev, [option]: !prev[option] };
            if (option === 'deepCdr' && next.deepCdr && !next.multiscanning) next.unarchiving = true;
            if (option === 'multiscanning' && !next.multiscanning && next.deepCdr) next.unarchiving = true;
            return next;
        });
    }, []);

    type SubmitFileExtras = {
        rulesOverride?: string;
        sandboxOverride?: boolean;
        archivePasswords?: string[];
        sendArchivePasswords?: boolean;
    };

    const submitFile = useCallback(
        (file: File, extras: SubmitFileExtras = {}) => {
            const { rulesOverride, sandboxOverride, archivePasswords = [], sendArchivePasswords = false } = extras;
            const rules = rulesOverride ?? buildScanRules(scanOptions);
            const sandbox = sandboxOverride ?? scanOptions.adaptiveSandbox;

            // Switching tabs unmounts the card; abort any in-flight upload to avoid
            // state updates and analytics firing after unmount.
            abortControllerRef.current?.abort();
            const controller = new AbortController();
            abortControllerRef.current = controller;

            setIsUploaded(true);
            setUploadProgress(0);
            setFileName(file.name);
            setFileSize(file.size);

            // Track submission once; progress can fire many times.
            trackCustomEvent('file_upload', { file_type: file.type, file_size: file.size, success: true });

            return scan(
                file,
                rules,
                sandbox,
                scanOptions.privateScan,
                archivePasswords,
                (progress: number) => {
                    if (!isMountedRef.current || controller.signal.aborted) return;
                    setUploadProgress(progress);
                },
                undefined,
                sendArchivePasswords,
                controller.signal,
            );
        },
        [scan, scanOptions, trackCustomEvent],
    );

    const submitUrl = useCallback(
        (
            downloadUrl: string,
            extras: Pick<SubmitFileExtras, 'rulesOverride'> & { onError?: (error: unknown) => boolean } = {},
        ) => {
            const rules = extras.rulesOverride ?? buildScanRules(scanOptions);

            abortControllerRef.current?.abort();
            const controller = new AbortController();
            abortControllerRef.current = controller;

            setIsUploaded(true);
            setUploadProgress(0);
            setFileName(downloadUrl);
            setFileSize(0);

            trackCustomEvent('file_upload', { file_type: 'url', file_size: 0, success: true });

            return scanUrl(
                downloadUrl,
                rules,
                scanOptions.adaptiveSandbox,
                scanOptions.privateScan,
                undefined,
                controller.signal,
                extras.onError,
            );
        },
        [scanUrl, scanOptions, trackCustomEvent],
    );

    const handleFileSelect = useCallback(
        (files: FileList | null, extras?: SubmitFileExtras) => {
            if (files && files.length > 0) {
                void submitFile(files[0], extras);
            }
        },
        [submitFile],
    );

    const triggerFileInput = useCallback(() => {
        setIsUploaded(true);
        fileInputRef.current?.click();
    }, []);

    const handleFileInputChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>, extras?: SubmitFileExtras) => {
            const files = e.target.files;
            if (files && files.length > 0) handleFileSelect(files, extras);
            else setIsUploaded(false);
            e.target.value = '';
        },
        [handleFileSelect],
    );

    return {
        // option state
        scanOptions,
        setScanOptions,
        handleCheckboxChange,
        isUnarchivingDisabled,
        // upload progress state
        isLoading,
        isUploaded,
        setIsUploaded,
        uploadProgress,
        fileName,
        fileSize,
        // file input / submit
        fileInputRef,
        triggerFileInput,
        handleFileInputChange,
        handleFileSelect,
        submitFile,
        submitUrl,
        // A hash is a lookup, not an upload — re-exported so a card owning this flow can route hash
        // input to the reputation endpoint instead of submitting it as a file.
        lookup,
        lookupSandbox,
    };
};
